import { formatPercentage, formatTimeAgo, formatValueWithCompactNumber } from '@/lib/format';
import { cn, genJudgingSizeColor } from '@/lib/utils';
import { IPumpToken } from '@/request/token/token.response.interface';
import { useAppStore } from '@/store';
import TokenAvatar from '@/widget/TokenAvatar';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface IProps {
  token: IPumpToken;
  activeIndex: number;
}

export default (props: IProps) => {
  const { token, activeIndex } = props;
  const { fwClass, lang, chainCoinPrice, chainInfo } = useAppStore();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const top10Class = BigNumber(token.top10).gte(0.15) ? 'text-sellColor' : genJudgingSizeColor(token?.top10);

  const solMarketCap = BigNumber(chainCoinPrice).times(360).toString();
  const solProgressValue = BigNumber(token.marketCap).div(solMarketCap).times(100).toFixed(1);
  const bscProgressValue = BigNumber(token.progress).times(100).toFixed(1);
  const solProgressText =
    activeIndex == 0
      ? solProgressValue + '%'
      : activeIndex == 1
      ? token?.isMigrating
        ? t('Token.migrating')
        : BigNumber(token?.marketCap).gt(solMarketCap)
        ? '99%'
        : solProgressValue + '%'
      : '100%';

  const bscProgressText =
    activeIndex == 0 ? bscProgressValue + '%' : activeIndex == 1 ? (token?.isMigrating ? t('Token.migrating') : bscProgressValue + '%') : '100%';

  const progressValue = chainInfo.chain == 'solana' ? solProgressValue : bscProgressValue;
  const progressText = chainInfo.chain == 'solana' ? solProgressText : bscProgressText;
  return (
    <div
      className="flex items-center text-[12px] h-[64px] border-b-[1px] border-borderColor"
      onClick={() => navigate(`/${chainInfo.path}/trade/${token?.address}`)}
    >
      <div className="flex-1">
        <div className="flex items-center gap-x-[8px]">
          <TokenAvatar logo={token?.logo} address={token?.address} className="size-[30px]" />
          <div>
            <p>
              <span className={cn('text-[14px] max-w-[80px] overflow-hidden text-ellipsis', fwClass)}>{token.symbol}</span>
              <span className="text-secondaryTextColor"> · </span>
              <span className="text-secondaryTextColor">{formatTimeAgo(Number(token.createTime), { lang, style: 'short' })}</span>
            </p>
            <p>
              <span className={cn(top10Class)}>{formatPercentage(token?.top10, { scale: true, showPositiveSign: false })}</span>
              <span> · </span>
              <span className={cn(genJudgingSizeColor(token?.dev))}>{formatPercentage(token?.dev, { scale: true, showPositiveSign: false })}</span>
            </p>
          </div>
        </div>
      </div>
      <div className="flex-[0.45]">
        <div className="flex justify-end h-[40px]">
          <div className="flex flex-col justify-around">
            <p className={cn('text-[14px] text-right', activeIndex < 2 ? 'text-primaryTextColor' : 'text-buyColor')}>{progressText}</p>
            <div className="h-[4px] w-[72px] bg-[#18181B1A] dark:bg-cardBgColor rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{ width: activeIndex < 2 ? progressValue + '%' : '100%', background: activeIndex < 2 ? '#FCD535' : '#0ECB81' }}
              ></div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-[0.6]">
        <div className="text-right">
          <p className="text-[14px]">{formatValueWithCompactNumber(token?.volume, true)}</p>
          <p className="text-secondaryTextColor">{formatValueWithCompactNumber(token?.marketCap, true)}</p>
        </div>
      </div>
    </div>
  );
};
